import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  TAccordion,
  TAccordionActionData,
  TAccordionEditableInfoActionData,
  TAccordionMoreInfo,
  TAccordionRowHeaderActionData,
  TAccordionTableVM,
} from './model/accordion-table.types';
import { ModalController, PopoverController } from '@ionic/angular';
import { ViewMoreListModalComponent } from '../view-more-list-modal/view-more-list-modal.component';
import { ACCORDION_TABLE_MODIFIER } from './model/accordion-table.modifier';
import { DropdownComponent } from '../dropdown/dropdown.component';

@Component({
  selector: 'monorepo-accordion-table',
  templateUrl: './accordion-table.component.html',
  styleUrls: ['./accordion-table.component.scss'],
})
export class AccordionTableComponent implements OnInit {
  @Input() accordionTableVM: TAccordionTableVM = { headers: [], accordions: [] };
  @Output() onListingVMUpdate = new EventEmitter<TAccordionTableVM>();
  @Output() onActionBtnClick = new EventEmitter<TAccordionActionData>();
  @Output() onEditableInfoClick = new EventEmitter<TAccordionEditableInfoActionData>();
  @Output() onRowHeaderActionClick = new EventEmitter<TAccordionRowHeaderActionData>();
  @Output() onAccordionRowDeleteClick = new EventEmitter<TAccordionTableVM['accordions'][number]['id']>();
  @Output() onAccordionRowClick = new EventEmitter<TAccordionTableVM['accordions'][number]['id']>();
  @Output() onSelectRow = new EventEmitter<TAccordionTableVM['accordions']>();

  constructor(private modalController: ModalController, private popoverController: PopoverController) {}

  ngOnInit() {
    this.filterActionsByFeatureCode();
  }

  filterActionsByFeatureCode() {
    this.accordionTableVM = {
      ...this.accordionTableVM,
      accordions: this.accordionTableVM.accordions.map((accordion) => ({
        ...accordion,
        actions: accordion.actions.filter((action) => !action.isHidden),
      })),
    };
  }

  trackByFn(_: number, item: TAccordionTableVM['accordions'][number]) {
    return item.id;
  }

  onToggle(cardId: TAccordionTableVM['accordions'][number]['id']) {
    const accordionTableVM = ACCORDION_TABLE_MODIFIER.toggleRow(this.accordionTableVM, cardId);
    this.onListingVMUpdate.emit(accordionTableVM);
    this.onAccordionRowClick.emit(cardId);
  }

  onDelete(event: Event, cardId: TAccordionTableVM['accordions'][number]['id']) {
    event.stopPropagation();
    const accordionTableVM = ACCORDION_TABLE_MODIFIER.deleteRow(this.accordionTableVM, cardId);
    this.onListingVMUpdate.emit(accordionTableVM);
    this.#emitSelectAccordionEvent(accordionTableVM);
    this.onAccordionRowDeleteClick.emit(cardId);
  }

  emitAction(actionId: TAccordionActionData['actionId'], cardId: TAccordionActionData['accordianId']) {
    this.onActionBtnClick.emit({ actionId, accordianId: cardId });
  }

  emitEditableInfoAction(
    actionId: TAccordionEditableInfoActionData['actionId'],
    cardId: TAccordionEditableInfoActionData['accordianId'],
  ) {
    this.onEditableInfoClick.emit({ actionId, accordianId: cardId });
  }

  emitRowHeaderAction(
    event: Event,
    actionId: TAccordionRowHeaderActionData['actionId'],
    cardId: TAccordionRowHeaderActionData['accordianId'],
  ) {
    event.stopPropagation();
    this.onRowHeaderActionClick.emit({ actionId, accordianId: cardId });
  }

  #emitSelectAccordionEvent(accordionModel: TAccordionTableVM) {
    const { accordions } = accordionModel;
    const selectedAccordions = accordions.filter((accordion) => accordion.accordionSelectionInfo?.isSelected);
    this.onListingVMUpdate.emit(accordionModel);
    this.onSelectRow.emit(selectedAccordions);
  }

  toggleSelectAccordion($event: Event, index: number) {
    const isCheckbox = ($event.target as HTMLDivElement).classList.contains('accordian-table__select-cell');
    if (!isCheckbox) {
      $event.preventDefault();
      return;
    }
    this.accordionTableVM = ACCORDION_TABLE_MODIFIER.selectAccordionByIndex(this.accordionTableVM, index);
    this.#emitSelectAccordionEvent(this.accordionTableVM);
  }

  toggleSelectAll($event: Event) {
    const isCheckbox = ($event.target as HTMLDivElement).classList.contains('accordian-table__select-cell');
    if (!isCheckbox) {
      $event.preventDefault();
      return;
    }
    this.accordionTableVM = ACCORDION_TABLE_MODIFIER.selectAllAccordions(this.accordionTableVM);
    this.#emitSelectAccordionEvent(this.accordionTableVM);
  }

  async openMoreActions(
    event: Event,
    accordionId: TAccordionTableVM['accordions'][number]['id'],
    accordion: TAccordion,
  ) {
    const moreActionsList = accordion.actions.slice(accordion.primaryActionsBtnCount ?? 2).map((action) => {
      return { displayText: action.displayName, type: action.actionId };
    });
    const popover = await this.popoverController.create({
      component: DropdownComponent,
      event: event,
      animated: false,
      componentProps: {
        items: moreActionsList,
      },
    });
    await popover.present();
    const dismissValue = await popover.onWillDismiss();
    if (dismissValue.data) {
      this.emitAction(dismissValue.data, accordionId);
    }
  }

  async onViewMore(label: string, values: TAccordionMoreInfo['items'][number]['values']) {
    const listModalComponent = await this.modalController.create({
      component: ViewMoreListModalComponent,
      componentProps: {
        label,
        values,
      },
      cssClass: 'ui-view-more-modal',
    });

    await listModalComponent.present();
  }

  getProgressPercentage(current: number, total: number) {
    return total > 0 ? Math.round((current / total) * 100) : 0;
  }
}
